import useEventCallback from '@hooks/use-event-callback';
import useEventListener from '@hooks/use-event-listener';
import parseJSON from '@utils/parse-json';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useState } from 'react';

declare global {
  interface WindowEventMap {
    'local-storage': CustomEvent;
  }
}

const IS_SERVER = typeof window === 'undefined';

type SetValue<T> = Dispatch<SetStateAction<T>>;

function useLocalStorage<T>(key: string, initialValue: T | (() => T)): [T, SetValue<T>, () => void] {
  // Get from local storage then
  // parse stored json or return initialValue
  const readValue = useCallback((): T => {
    const initialValueToUse = initialValue instanceof Function ? initialValue() : initialValue;

    // Prevent build error "window is undefined" but keeps working
    if (IS_SERVER) {
      return initialValueToUse;
    }

    try {
      const item = window.localStorage.getItem(key);
      return item ? (parseJSON(item) as T) : initialValueToUse;
    } catch (error) {
      console.warn(`Error reading localStorage key “${key}”:`, error);
      return initialValueToUse;
    }
  }, [initialValue, key]);

  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(readValue);

  const resetValue = useCallback(() => {
    // Prevent build error "window is undefined" but keeps working
    if (IS_SERVER) {
      console.warn(`Tried resetting localStorage key “${key}” even though environment is not a client`);
    }

    // Remove the key from local storage
    window.localStorage.removeItem(key);

    // Save state with default value
    setStoredValue(readValue());

    // We dispatch a custom event so every useLocalStorage hook are notified
    window.dispatchEvent(
      new StorageEvent('local-storage', {
        storageArea: window.localStorage,
        url: window.location.href,
        key,
        newValue: null,
      }),
    );
  }, [key, readValue]);

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue: SetValue<T> = useEventCallback((value) => {
    // Prevent build error "window is undefined" but keeps working
    if (IS_SERVER) {
      console.warn(`Tried setting localStorage key “${key}” even though environment is not a client`);
    }

    try {
      // Allow value to be a function, so we have the same API as useState
      const newValue = value instanceof Function ? value(readValue()) : value;
      const jsonValue = JSON.stringify(newValue);

      // Save to local storage
      window.localStorage.setItem(key, jsonValue);

      // Save state
      setStoredValue(newValue);

      // We dispatch a custom event so every similar useLocalStorage hook is notified
      window.dispatchEvent(
        new StorageEvent('local-storage', {
          storageArea: window.localStorage,
          url: window.location.href,
          key,
          newValue: jsonValue,
        }),
      );
    } catch (error) {
      console.error(`Error setting localStorage key “${key}”:`, error);
    }
  });

  useEffect(() => {
    setStoredValue(readValue());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const handleStorageChange = useCallback(
    (event: StorageEvent | CustomEvent) => {
      if (
        ((event as StorageEvent)?.key && (event as StorageEvent).key !== key) ||
        (event as StorageEvent).storageArea !== window.localStorage
      ) {
        return;
      }
      setStoredValue(readValue());
    },
    [key, readValue],
  );

  // this only works for other documents, not the current one
  useEventListener('storage', handleStorageChange);

  // this is a custom event, triggered in writeValueToLocalStorage
  // See: useLocalStorage()
  useEventListener('local-storage', handleStorageChange);

  return [storedValue, setValue, resetValue];
}

export default useLocalStorage;
